import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { telHref } from 'src/utils/supportAvailability.util';

/**
 * Every `tel:` and `mailto:` link in the app goes through these two, so a phone number or an
 * address never splits mid-token on a narrow screen. 2.28.0 shipped the support card with
 * `break-all`, which broke "(800) 601-4678" into "(80" / "0) 601-4678" on an iPhone.
 *
 *  - A phone number never wraps; it moves to the next line whole.
 *  - An email address wraps only when it cannot fit on a line by itself. `overflow-wrap: anywhere`
 *    (unlike `break-word`) also lowers its min-content width, so a long address inside a flex row
 *    shrinks instead of pushing the page sideways.
 *
 * Raw `tel:`/`mailto:` hrefs and `break-all` are lint errors elsewhere (eslint.config.js).
 */
type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { children?: ReactNode };

export const PhoneLink = ({ phone, className, children, ...rest }: AnchorProps & { phone: string }) => (
   <a href={telHref(phone)} className={twMerge('whitespace-nowrap', className)} {...rest}>
      {children ?? phone}
   </a>
);

export const EmailLink = ({ email, className, children, ...rest }: AnchorProps & { email: string }) => (
   <a href={`mailto:${email}`} className={twMerge('[overflow-wrap:anywhere]', className)} {...rest}>
      {children ?? email}
   </a>
);
